import type { DbEnv } from "common/constants/db";
import type { ReleaseBranch, Server } from "common/constants/env";
import { POSTHOG_API_KEY } from "common/constants/marketing";

/* eslint-disable @typescript-eslint/naming-convention */
export interface EnvDetails {
  DEVELOP: boolean;
  STAGE: boolean;
  PROD: boolean;
  NOT_PROD: boolean;
  NOT_STAGE_OR_PROD: boolean;
  STAGE_OR_PROD: boolean;
}

interface FlagsConfig {
  DEBUG?: string;
  TEST_E2E_MODE?: string;
  GIT_TAG?: string;
  GIT_COMMIT_HASH?: string;
  GIT_BRANCH?: string;
  IS_DEV_MODE?: string;
  DB_ENV?: DbEnv;
  COGNITO_ENV?: Server;
  COGNITO_CLIENT_ID?: string;
  COGNITO_USER_POOL_ID?: string;
  RENDER_DEBUG?: string;
}

/**
 * Exposes build and configuration-related constants. Valid only
 * on the client-side, and unusable before next(...) has been called.
 */
export default class Flags {
  // Initializing these so that they can overwritten for tests
  public static config: FlagsConfig = {
    DEBUG: process.env.PUBLIC_DEBUG,
    TEST_E2E_MODE: process.env.PUBLIC_TEST_E2E_MODE,
    GIT_TAG: process.env.PUBLIC_GIT_TAG,
    GIT_COMMIT_HASH: process.env.PUBLIC_GIT_COMMIT_HASH,
    GIT_BRANCH: process.env.PUBLIC_GIT_BRANCH,
    IS_DEV_MODE: process.env.PUBLIC_IS_DEV_MODE,
    DB_ENV: process.env.PUBLIC_DB_ENV as DbEnv,
    COGNITO_ENV: process.env.PUBLIC_COGNITO_ENV as Server,
    COGNITO_CLIENT_ID: process.env.PUBLIC_COGNITO_CLIENT_ID,
    COGNITO_USER_POOL_ID: process.env.PUBLIC_COGNITO_USER_POOL_ID,
    RENDER_DEBUG: process.env.PUBLIC_RENDER_DEBUG,
  };

  public static absoluteUrl(relativeUrl: string): string {
    return window.location.origin + relativeUrl;
  }

  public static get TEST_E2E_MODE(): boolean {
    if (typeof Flags.config.TEST_E2E_MODE === "string") {
      return Flags.config.TEST_E2E_MODE === "true";
    }
    return !!Flags.config.TEST_E2E_MODE;
  }

  public static get GIT_TAG(): string {
    return Flags.config.GIT_TAG ?? "";
  }

  public static get GIT_COMMIT_HASH(): string {
    return Flags.config.GIT_COMMIT_HASH ?? "";
  }

  public static get GIT_BRANCH(): string {
    return Flags.config.GIT_BRANCH ?? "";
  }

  public static get DEBUG(): boolean {
    if (typeof Flags.config.DEBUG === "string") {
      return Flags.config.DEBUG === "true";
    }

    return !!Flags.config.DEBUG;
  }

  public static get IS_DEV_MODE(): boolean {
    if (typeof Flags.config.IS_DEV_MODE === "string") {
      return Flags.config.IS_DEV_MODE === "true";
    }

    return !!Flags.config.IS_DEV_MODE;
  }

  public static get DB_ENV(): DbEnv {
    return Flags.config.DB_ENV as DbEnv;
  }

  public static get COGNITO_ENV(): Server {
    return Flags.config.COGNITO_ENV as Server;
  }

  public static get COGNITO_CLIENT_ID(): string {
    return Flags.config.COGNITO_CLIENT_ID ?? "";
  }

  public static get COGNITO_USER_POOL_ID(): string {
    return Flags.config.COGNITO_USER_POOL_ID ?? "";
  }

  public static get RENDER_DEBUG(): boolean {
    if (typeof Flags.config.RENDER_DEBUG === "string") {
      return Flags.config.RENDER_DEBUG === "true";
    }
    return !!Flags.config.RENDER_DEBUG;
  }

  private static usingBranch(branchName: ReleaseBranch): boolean {
    return Flags.GIT_BRANCH === branchName;
  }

  public static get APP_ENV(): EnvDetails {
    return {
      DEVELOP: Flags.usingBranch("develop"),
      STAGE: Flags.usingBranch("stage"),
      PROD: Flags.usingBranch("master"),
      NOT_PROD: !Flags.usingBranch("master"),
      NOT_STAGE_OR_PROD: !Flags.usingBranch("master") && !Flags.usingBranch("stage"),
      STAGE_OR_PROD: Flags.usingBranch("stage") || Flags.usingBranch("master"),
    } as const;
  }

  public static get POSTHOG_API_KEY(): string {
    // Only capture events on prod or debug mode
    return Flags.config.DB_ENV === "prod" || Flags.DEBUG ? POSTHOG_API_KEY : "";
  }

  public static print(): void {
    console.log(
      JSON.stringify(
        {
          DEBUG: Flags.DEBUG,
          GIT_BRANCH: Flags.GIT_BRANCH,
          GIT_COMMIT_HASH: Flags.GIT_COMMIT_HASH,
          GIT_TAG: Flags.GIT_TAG,
          IS_DEV_MODE: Flags.IS_DEV_MODE,
          TEST_E2E_MODE: Flags.TEST_E2E_MODE,
          DB_ENV: Flags.DB_ENV,
          COGNITO_ENV: Flags.COGNITO_ENV,
          RENDER_DEBUG: Flags.RENDER_DEBUG,
        },
        null,
        2,
      ),
    );
  }
}
